/**
 * After "Initiate Run", task_id may arrive on that converse response or on any
 * later agent turn in the same conversation. These helpers scan message cache
 * metadata from a baseline index and optionally wait for subsequent updates.
 */

import type { ESACacheMessage } from '../cache/MessageCache';
import { getMessageCache } from '../cache/messageCacheInstance';

/** Default wait for task_id on initiate or follow-up turns. */
export const INITIATE_TASK_ID_TIMEOUT_MS = 90_000;

export function extractTaskIdFromMessage(msg: ESACacheMessage | undefined): string {
  const raw = msg?.metadata?.task_id;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : '';
}

/** First non-empty metadata.task_id at or after fromIndex (inclusive). */
export function findTaskIdFromIndex(msgs: ESACacheMessage[], fromIndex: number): string {
  const start = Math.max(0, fromIndex);
  for (let i = start; i < msgs.length; i++) {
    const id = extractTaskIdFromMessage(msgs[i]);
    if (id) return id;
  }
  return '';
}

/**
 * Resolve when a message at/after fromIndex carries metadata.task_id, or when
 * timeout/abort fires (resolves to '').
 */
export function waitForTaskIdAfterInitiate(
  conversationId: string,
  fromIndex: number,
  options?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<string> {
  const timeoutMs = options?.timeoutMs ?? INITIATE_TASK_ID_TIMEOUT_MS;
  const cache = getMessageCache();
  const signal = options?.signal;

  const peek = (): string => findTaskIdFromIndex(cache.getSnapshot(conversationId), fromIndex);

  const immediate = peek();
  if (immediate) return Promise.resolve(immediate);
  if (signal?.aborted) return Promise.resolve('');

  return new Promise(resolve => {
    let settled = false;

    const finish = (taskId: string): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      signal?.removeEventListener('abort', onAbort);
      resolve(taskId);
    };

    const onAbort = (): void => finish('');

    const unsub = cache.subscribe(() => {
      const found = peek();
      if (found) finish(found);
    });

    const timer = setTimeout(() => finish(''), timeoutMs);
    signal?.addEventListener('abort', onAbort);

    // Race: message may land between peek() and subscribe().
    const again = peek();
    if (again) finish(again);
  });
}
